import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes, MedicalDataTypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { useMemo } from 'react';
import {
    getProfileResourcesForCategoryCacheKey,
    getResourcesCountCacheKey,
    onResourceBulkCreate,
    onResourceBulkUpdate,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useNotify from '../useNotify';

const intl = getIntl('UseMedicalProfileData', {
    resourceLoadCountsFailure: 'Failed to load resource counts',
    resourceCategoryLoadFailure: 'Failed to load resources',
    successfulBulkUpdate: 'Successfully updated data',
    successfulBulkDelete: 'Successfully deleted data',
    bulkUpdateError: 'Failed to update data',
    bulkDeleteError: 'Failed to delete data',
    bulkCreateError: 'Failed to create data',
    bulkCreateSuccess: 'Successfully created data',
});

export type UseMedicalProfileArgs = {
    medicalProfileId: string | null;
    resourceCategory?: MedicalDataTypes.PrimaryRecordResourceCategory;
    onBulkUpdateSuccess?: () => void;
    onBulkCreateSuccess?: () => void;
};

export type CreateEmergencyContactArgs = {
    emergencyContactDetails: APITypes.medicalProfileService.CreateEmergencyContactDetailsBody;
    medicalProfileId: string;
};

export type BulkCreateArgs = {
    category: MedicalDataTypes.PrimaryRecordResourceCategory;
    bulkCreateBody: APITypes.medicalDataService.BulkCreateResourcesRequest<MedicalDataTypes.EditablePrimaryRecordResource>;
};

export type UseMedicalProfileDataReturnValue = {
    resourceCounts: APITypes.medicalDataService.ResourceCountsResponse | null;
    isLoadingResourceCounts: boolean;
    resources: APITypes.medicalDataService.ListResourcesResponse<MedicalDataTypes.PrimaryRecordResource>;
    isLoadingResources: boolean;
    groupedResources: GroupedResource[];
    bulkUpdateResources: (args: APITypes.medicalDataService.BulkEditRequest) => void;
    isBulkUpdatingResources: boolean;
    bulkCreateResources: (args: BulkCreateArgs) => void;
    isBulkCreatingResources: boolean;
    updateVisitSeries: (args: APITypes.medicalDataService.UpdateVisitSeriesRequest) => void;
    isUpdatingVisitSeries: boolean;
};

export type GroupedResource =
    APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource> & {
        groupSubResources: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>[];
    };

export function isGroupedResource(
    resource: APITypes.medicalDataService.ListResourceResponseItem<MedicalDataTypes.PrimaryRecordResource>,
): resource is GroupedResource {
    return 'groupSubResources' in resource;
}

export const getAllResourceIdsFromGroupedResources = (
    groupedResources: GroupedResource[],
): APITypes.medicalDataService.BulkEditRequest['resourceIds'] => {
    return [
        ...new Set(
            groupedResources.reduce((acc, _) => {
                acc.push(_.id);
                acc.push(..._.groupSubResources.map((_) => _.id));
                return acc;
            }, [] as string[]),
        ),
    ];
};

const getSortValue = (resource: MedicalDataTypes.PrimaryRecordResource) => {
    const getTime = (dateTime?: string) => (dateTime ? new Date(dateTime).getTime() : 0);

    if (resource.type === 'allergy') {
        return getTime(resource.startDateTime);
    } else if (resource.type === 'medication') {
        return getTime(resource.startDateTime);
    } else if (resource.type === 'visit') {
        return getTime(resource.startDateTime);
    } else if (resource.type === 'condition') {
        return getTime(resource.startDateTime);
    } else {
        return 0;
    }
};

export default function useMedicalProfileData({
    medicalProfileId,
    resourceCategory,
    onBulkUpdateSuccess,
    onBulkCreateSuccess,
}: UseMedicalProfileArgs): UseMedicalProfileDataReturnValue {
    const notify = useNotify();
    const sort = 'descending';
    const resourceCountsQuery = useQuery({
        queryKey: getResourcesCountCacheKey(medicalProfileId || ''),
        queryFn: async () => {
            return await appAPI.get(urls.api.medicalData.counts(medicalProfileId!));
        },
        enabled: !!medicalProfileId,
    });
    useQueryErrorNotify(resourceCountsQuery, intl('resourceLoadCountsFailure'));

    const resourcesQuery = useQuery({
        queryKey: getProfileResourcesForCategoryCacheKey(medicalProfileId!, resourceCategory!, sort),
        queryFn: async () => {
            return await appAPI.get<
                APITypes.medicalDataService.ListResourcesResponse<MedicalDataTypes.PrimaryRecordResource>
            >(urls.api.medicalData.resources(medicalProfileId!, resourceCategory!, sort));
        },
        enabled: !!resourceCategory && !!medicalProfileId,
    });
    useQueryErrorNotify(resourcesQuery, intl('resourceCategoryLoadFailure'));

    const groupedResources: GroupedResource[] = useMemo(() => {
        if (!resourcesQuery.data) {
            return [];
        }

        return resourcesQuery.data.resources
            .filter((_) => _.groupingMetadata.isPrimary)
            .map((primaryResource) => ({
                ...primaryResource,
                groupSubResources: resourcesQuery.data.resources.filter(
                    (_) =>
                        _.id !== primaryResource.id &&
                        _.groupingMetadata.groupId === primaryResource.groupingMetadata.groupId,
                ),
            }))
            .map((_) => ({
                ..._,
                sortValue: getSortValue(_),
            }))
            .sort((a, b) => {
                if (sort === 'descending') {
                    return b.sortValue - a.sortValue;
                } else {
                    return a.sortValue - b.sortValue;
                }
            })
            .map((_) => {
                const { sortValue: _sortValue, ...rest } = _;
                return rest;
            });
    }, [resourcesQuery.data]);

    const bulkUpdateResourcesMutation = useMutation({
        mutationFn: async (args: APITypes.medicalDataService.BulkEditRequest) => {
            if (!medicalProfileId) {
                console.error('medicalProfileId is required for bulk update');
                return;
            }

            // Chunked: a long id list (e.g. deleting a whole recurring series) exceeds the
            // gateway's request body size limit in one call.
            const CHUNK_SIZE = 50;
            for (let i = 0; i < args.resourceIds.length; i += CHUNK_SIZE) {
                await appAPI.post(urls.api.medicalData.bulkUpdate(medicalProfileId), {
                    body: { ...args, resourceIds: args.resourceIds.slice(i, i + CHUNK_SIZE) },
                });
            }
        },
        onSuccess: (_, args) => {
            // reset immediately and then delay for any lags in eventual consistency
            onResourceBulkUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            setTimeout(() => {
                onResourceBulkUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            }, 3000);
            onBulkUpdateSuccess?.();
            if (args.action === 'delete') {
                notify('success', intl('successfulBulkDelete'));
            } else {
                notify('success', intl('successfulBulkUpdate'));
            }
        },
        onError: (_, args) => {
            if (args.action === 'delete') {
                notify('error', intl('bulkDeleteError'));
            } else {
                notify('error', intl('bulkUpdateError'));
            }
        },
    });

    const bulkCreateResourcesMutation = useMutation({
        mutationFn: async (args: BulkCreateArgs) => {
            if (medicalProfileId === null) {
                console.error('medicalProfileId is required for bulk create');
                return;
            }

            await appAPI.post(urls.api.medicalData.bulkCreate(medicalProfileId, args.category), {
                body: args.bulkCreateBody,
            });
        },
        onSuccess: () => {
            onResourceBulkCreate.resetCache({ medicalProfileId: medicalProfileId! })();
            onBulkCreateSuccess?.();
            notify('success', intl('bulkCreateSuccess'));
        },
        onError: () => {
            notify('error', intl('bulkCreateError'));
        },
    });

    const updateVisitSeriesMutation = useMutation({
        mutationFn: async (args: APITypes.medicalDataService.UpdateVisitSeriesRequest) => {
            if (!medicalProfileId) {
                console.error('medicalProfileId is required for series update');
                return;
            }
            // No retry: the request isn't idempotent on the invite side (a re-run would bump
            // invite sequences and re-send calendar updates), so a lost response must not
            // silently replay the whole series update.
            await appAPI.post(urls.api.medicalData.updateVisitSeries(medicalProfileId), {
                body: args,
                retryStrategy: { strategy: 'no-retry' },
            });
        },
        retry: false,
        onSuccess: () => {
            onResourceBulkUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            setTimeout(() => {
                onResourceBulkUpdate.resetCache({ medicalProfileId: medicalProfileId! })();
            }, 3000);
            onBulkUpdateSuccess?.();
            notify('success', intl('successfulBulkUpdate'));
        },
        onError: () => {
            notify('error', intl('bulkUpdateError'));
        },
    });

    return {
        resourceCounts: resourceCountsQuery.data ?? null,
        isLoadingResourceCounts: resourceCountsQuery.isLoading,
        resources: resourcesQuery.data ?? {
            resources: [],
        },
        isLoadingResources: resourcesQuery.isLoading,
        groupedResources,
        bulkUpdateResources: bulkUpdateResourcesMutation.mutate,
        isBulkUpdatingResources: bulkUpdateResourcesMutation.isPending,
        bulkCreateResources: bulkCreateResourcesMutation.mutate,
        isBulkCreatingResources: bulkCreateResourcesMutation.isPending,
        updateVisitSeries: updateVisitSeriesMutation.mutate,
        isUpdatingVisitSeries: updateVisitSeriesMutation.isPending,
    };
}
